import { Card, IconButton, Icon, Box, Chip, Tooltip, Typography, Avatar, Badge } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useState, useEffect } from "react";
import DataTable from "../../../../Components/Table/DataTable";
import WDBox from "../../../../MaterialComponents/WDBox";
import moment from "moment";
import AppService from "../../../../Configurations/Appservice";
import TableLoader from "../../../../Components/AppLoader/TableLoader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { getIconByName, COLOR_PRESETS } from "../../../../Components/IconSelector";

// Theme color (teal)
const THEME_COLOR = '#009688';
const THEME_COLOR_LIGHT = '#e0f2f1';

// Target type configurations with theme-matching styling
const TARGET_TYPE_CONFIG = {
  EMPLOYEE: {
    icon: PersonIcon,
    label: 'Employee',
    color: THEME_COLOR,
    bgColor: THEME_COLOR_LIGHT,
    description: 'Employee services'
  },
  ORGANIZATION: {
    icon: BusinessIcon,
    label: 'Organization',
    color: '#5c6bc0', // Indigo for organization
    bgColor: '#e8eaf6',
    description: 'Organization services'
  },
  BOTH: {
    icon: GroupsIcon,
    label: 'Both',
    color: '#7b1fa2', // Purple for both
    bgColor: '#f3e5f5',
    description: 'Employee & Organization'
  }
};

// Helper to render target type with enhanced styling
const renderTargetType = (targetType) => {
  const config = TARGET_TYPE_CONFIG[targetType] || TARGET_TYPE_CONFIG.EMPLOYEE;
  const IconComponent = config.icon;

  return (
    <Tooltip title={config.description} arrow placement="top">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: '20px',
          backgroundColor: config.bgColor,
          border: `1px solid ${config.color}30`,
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: `0 2px 8px ${config.color}30`,
            transform: 'translateY(-1px)'
          }
        }}
      >
        <Avatar
          sx={{
            width: 22,
            height: 22,
            backgroundColor: config.color,
          }}
        >
          <IconComponent sx={{ fontSize: 14, color: '#fff' }} />
        </Avatar>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: config.color,
            letterSpacing: '0.3px'
          }}
        >
          {config.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Helper to render services count with badge styling
const renderServicesCount = (count) => {
  const hasServices = count > 0;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 36,
        height: 28,
        px: 1.5,
        borderRadius: '14px',
        backgroundColor: hasServices ? THEME_COLOR_LIGHT : '#fafafa',
        border: hasServices ? `1px solid ${THEME_COLOR}40` : '1px solid #e0e0e0',
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: hasServices ? THEME_COLOR : '#bdbdbd',
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};

// Helper to render date with friendly format
const renderDate = (dateString) => {
  const date = moment.utc(dateString).local();
  const now = moment();
  const diffDays = now.diff(date, 'days');

  let displayText;
  if (diffDays === 0) {
    displayText = 'Today';
  } else if (diffDays === 1) {
    displayText = 'Yesterday';
  } else if (diffDays < 7) {
    displayText = `${diffDays} days ago`;
  } else {
    displayText = date.format('MMM D, YYYY');
  }

  return (
    <Tooltip title={date.format('MMMM D, YYYY [at] h:mm A')} arrow placement="top">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: '#616161',
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
          {displayText}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Helper to render group name with icon and better typography
const renderGroupName = (name, numberOfServices, icon, color) => {
  const IconComponent = icon ? getIconByName(icon) : null;
  const displayColor = color || THEME_COLOR;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {/* Icon display */}
      {IconComponent && (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            backgroundColor: displayColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            flexShrink: 0,
          }}
        >
          <IconComponent sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
      )}
      {/* Name and service count */}
      <Box>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: '#212121',
            mb: 0.25,
            '&:hover': {
              color: THEME_COLOR,
            }
          }}
        >
          {name}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#9e9e9e' }}>
          {numberOfServices} {numberOfServices === 1 ? 'service' : 'services'} configured
        </Typography>
      </Box>
    </Box>
  );
};

// Helper to render edit action
const renderEditAction = (onClick) => {
  return (
    <Tooltip title="Edit Service Group" arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          p: 0.75,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: THEME_COLOR,
            borderColor: THEME_COLOR,
            '& .MuiSvgIcon-root': {
              color: '#fff',
            }
          }
        }}
      >
        <EditIcon sx={{ fontSize: 18, color: '#616161' }} />
      </IconButton>
    </Tooltip>
  );
};

const tableInitialData = {
  columns: [
    { Header: "Target", accessor: "targetDisplay", width: "14%" },
    { Header: "Group Name", accessor: "groupNameDisplay", width: "40%" },
    { Header: "Services", accessor: "servicesDisplay", width: "10%", align: "center" },
    { Header: "Created", accessor: "dateDisplay", width: "14%" },
    { Header: "", accessor: "edit", width: "8%", align: "center" },
  ],
  rows: [],
};

const tableFilters = [
  {
    accessor: "name",
    type: "TEXT",
    labelName: "Search",
  },
];

const ServiceGroups = () => {
  const [tableData, setTableData] = useState(tableInitialData);
  const [isLoading, loading] = useState(true);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateScreenTitle("Service Groups"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const _generateTableRows = (response) => {
    const rows = response.map((item) => {
      const targetType = item.targetType || 'EMPLOYEE';

      return {
        ...item,
        targetDisplay: renderTargetType(targetType),
        groupNameDisplay: renderGroupName(item.name, item.numberOfServices || 0, item.icon, item.color),
        servicesDisplay: renderServicesCount(item.numberOfServices || 0),
        dateDisplay: renderDate(item.createDateTime),
        edit: renderEditAction(() => _navigateToEditScreen(item)),
      };
    });

    let tableDataDuplicate = { ...tableData };
    tableDataDuplicate.rows = rows;
    setTableData(tableDataDuplicate);
  };

  useEffect(() => {
    AppService.get("v1/serviceGroup")
      .then(_generateTableRows)
      .catch((error) => {})
      .finally(() => {
        loading(false);
      });
  }, []);

  const _navigateToCreationScreen = () => {
    navigate("/ADM-ServiceGroup/adm-new-serviceGroup");
  };

  const _navigateToEditScreen = (group) => {
    navigate("/ADM-ServiceGroup/adm-new-serviceGroup", { state: { group } });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <DataTable
        table={tableData}
        showAddButton={true}
        addButtonOnClick={() => _navigateToCreationScreen()}
        addButtonLabel="New Service Group"
      />
    </Box>
  );
};

export default ServiceGroups;
